import { clone } from "ramda";

import type WebApiClient from "../../api/WebApiClient.js";
import {
  CREATE_SEARCH_RESULT_FAILURE,
  CREATE_SEARCH_RESULT_REQUEST,
  CREATE_SEARCH_RESULT_SUCCESS,
  LOAD_SEARCH_RESULT_FAILURE,
  LOAD_SEARCH_RESULT_REQUEST,
  LOAD_SEARCH_RESULT_SUCCESS,
} from "../action-types.js";
import { type IAppState } from "../reducer.js";
import { createReducer } from "../redux-utils.js";
import { createErrorHandler } from "@/app-utils/errors.ts";
import { pushRoute } from "@/app-utils/route.ts";
import { type IRsqlFilterTree, encodeFilter } from "@/app-utils/rsql.ts";
import {
  type AdvancedSearchParams,
  type Group,
  type InputAdvancedSearchInput,
  type InputSearchEntityTypeInput,
  type SearchResult,
  type SearchScope,
} from "@/graphql.ts";
import { SEARCH_RESTRICTION_TYPES } from "@/settings/settings.ts";

/**
 * State definition
 */
export interface ISearchResultState {
  id: any;
  name: string;
  groups: Group[];
  isLoading: boolean;
  advancedSearchParams: AdvancedSearchParams;
}

const initialState: ISearchResultState = {
  id: 0,
  isLoading: false,
  name: "",
  groups: [],
  advancedSearchParams: null,
};

/**
 * Reducers
 */
export default createReducer(initialState, {
  // Note that the search indicatator flag is set by the global-search reducer (using the same action type).
  [CREATE_SEARCH_RESULT_SUCCESS]: (state: ISearchResultState, payload: SearchResult) => {
    return { ...state, ...payload, isLoading: false };
  },
  [CREATE_SEARCH_RESULT_REQUEST]: (state: ISearchResultState) => {
    return { ...state, isLoading: true };
  },
  [LOAD_SEARCH_RESULT_REQUEST]: (state: ISearchResultState) => {
    return { ...state, isLoading: true, groups: [] };
  },
  [LOAD_SEARCH_RESULT_SUCCESS]: (state: ISearchResultState, payload: SearchResult) => {
    return { ...state, isLoading: false, ...payload };
  },
  [LOAD_SEARCH_RESULT_FAILURE]: (state: ISearchResultState) => {
    return { ...state, ...initialState };
  },
});

/**
 * Action creators
 */
export function shouldLoadSearchResult(searchResultId: number) {
  return (dispatch, getState, webApiClient: WebApiClient): Promise<SearchResult> => {
    const state: IAppState = getState();

    if (searchResultId !== state.searchResults.id) {
      // Only fetch search results if they are not already in the app state
      dispatch({ type: LOAD_SEARCH_RESULT_REQUEST });

      return webApiClient
        .fetchSearchResult(searchResultId)
        .then((response: SearchResult) => {
          dispatch({
            type: LOAD_SEARCH_RESULT_SUCCESS,
            payload: response,
          });
          return response;
        })
        .catch(
          createErrorHandler(dispatch, {
            actionType: LOAD_SEARCH_RESULT_FAILURE,
            alertTitle: "Fetching of Search Results Failed",
            alertMessage: `Error fetching search results group "${searchResultId}"`,
          }),
        );
    } else {
      const result: SearchResult = {
        name: state.searchResults.name,
        id: state.searchResults.id,
        advancedSearchParams: clone(state.searchResults.advancedSearchParams),
        groups: clone(state.searchResults.groups),
      };
      return Promise.resolve(result);
    }
  };
}

// * Called when one of the options in the global search suggestion popup is clicked:
// * "View All", specific entity (e.g. genes) or specific items
// * In all cases, the running search is indicated to the state, a search result
//   is created and the link is adapted to point to that search result (via SRT_ID)
export function createSearchResultAndRedirect(searchTerm: string, types: InputSearchEntityTypeInput[]) {
  return (dispatch, _getState, webApiClient: WebApiClient) => {
    dispatch(createSearchResultRequest());

    return webApiClient
      .saveSearchResult(searchTerm, types)
      .then((searchResult: SearchResult) => {
        // Save the new search result in the app state (using both the global-search
        // and the search-result reducer))
        dispatch(createSearchResultSuccess(searchResult));
        // Redirect to result list page
        pushRoute(`/search/${searchResult.id}`);
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: CREATE_SEARCH_RESULT_FAILURE,
          alertTitle: "Creation of Search Results Group Failed",
        }),
      );
  };
}

export function createSearchResultFromURL(searchType: string, queryExpression: string) {
  return (dispatch, _getState, webApiClient: WebApiClient): Promise<SearchResult> => {
    const advancedSearchInput: InputAdvancedSearchInput = {
      id: Math.floor(Math.random() * 1000),
      searchType,
      quickSearchQuery: undefined,
      attributesQueryExpression: queryExpression,
      restrictionType: SEARCH_RESTRICTION_TYPES.GENOME,
      restrictions: undefined,
    };

    dispatch(createSearchResultRequest());
    // dispatch(auditCreateSearchResut());
    return webApiClient
      .saveAdvancedSearchResult(advancedSearchInput)
      .then((searchResult: SearchResult) => {
        // Save the new search result in the app state (using both the global-search
        // and the search-result reducer))
        dispatch(createSearchResultSuccess(searchResult));
        return searchResult;
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: CREATE_SEARCH_RESULT_FAILURE,
          rejectPromise: true,
        }),
      );
  };
}

export function createAdvancedSearchResultAndRedirect(
  searchType: string,
  searchSubTypeId: number,
  quickSearchQuery: string,
  filterConstraint: IRsqlFilterTree,
  scopeRestriction: SearchScope[],
) {
  return (dispatch, _getState, webApiClient: WebApiClient): Promise<SearchResult> => {
    const attributesQueryExpression: string = encodeFilter(filterConstraint);

    // restrictionType must not be null, therefore we simply set it to genome if no restrictions are in place
    const restrictionType: string =
      scopeRestriction && scopeRestriction.length > 0
        ? scopeRestriction[0].restrictionType
        : SEARCH_RESTRICTION_TYPES.GENOME;

    const advancedSearchInput: InputAdvancedSearchInput = {
      id: Math.floor(Math.random() * 1000),
      searchType,
      searchSubTypeId,
      quickSearchQuery: quickSearchQuery || undefined,
      attributesQueryExpression: attributesQueryExpression || undefined, // undefined allows to remove the property when stringifying to json
      restrictionType: restrictionType,
      restrictions: JSON.stringify(scopeRestriction || undefined),
    };

    dispatch(createSearchResultRequest());
    // dispatch(auditCreateSearchResut());
    return webApiClient
      .saveAdvancedSearchResult(advancedSearchInput)
      .then((searchResult: SearchResult) => {
        // Save the new search result in the app state (using both the global-search
        // and the search-result reducer))
        dispatch(createSearchResultSuccess(searchResult));
        // Redirect to result list page
        pushRoute(`/search/${searchResult.id}`);
        return searchResult;
      })
      .catch(
        createErrorHandler(dispatch, {
          actionType: CREATE_SEARCH_RESULT_FAILURE,
          showAlert: false,
          throwError: true,
        }),
      );
  };
}

function createSearchResultRequest() {
  return {
    type: CREATE_SEARCH_RESULT_REQUEST,
  };
}

function createSearchResultSuccess(searchResult: SearchResult) {
  return {
    type: CREATE_SEARCH_RESULT_SUCCESS,
    payload: searchResult,
  };
}
